// @flow
import {
	KIND,
	MIME_TYPE,
	CORPORA,
	PREDEFINED_TREE_NODES,
} from "tabs/GoogleDrive/data/consts";
import appConfig from "appConfig";

const getImageDimensions = ({
	imageMediaMetadata: { width, height } = {},
}): { width: number, height: number } => ({
	width,
	height,
});

type GoogleDriveItem = {
    extension: string,
    metadata: string,
    width: number,
    height: number,
    thumbnail: string,
    link: string,
    url: string,
    title: string,
    type: string,
    kind: string,
    driveId: string,
	resourceKey?:string
};

export const isDrive = (kind: string = ""): boolean => kind.includes("#drive");

type composeOrderByParams = {
    kind: string,
    orderBy: string,
    sort: string,
    term: string
};

export const composeOrderBy = ({
	kind,
	orderBy,
	sort,
	term,
}: composeOrderByParams): Object => {
	if (term) {return {};}
	const result = [];

	if (kind === KIND.RECENT) {
		result.push(KIND.RECENT);
	} else if (orderBy) {
		result.push(orderBy);
	}

	if (sort) {
		result.push(sort);
	}

	return {
		orderBy: result.join(" "),
	};
};

export const composeSearchQuery = ({
	term,
	parentId,
	kind,
	onlyFolders,
}: Object) => {
	const q = ["trashed = false"];
	if (term) {
		q.push(`(name contains '${term}' OR fullText contains '${term}')`);
	}
	q.push(
		`mimeType ${
			onlyFolders ? "=" : "!="
		} 'application/vnd.google-apps.folder'`,
	);

	if (kind === KIND.STARRED || kind === KIND.SHARED_WITH_ME) {
		q.push(kind);
	} else if (parentId && kind !== KIND.RECENT) {
		q.push(`'${parentId}' in parents`);
	}

	return q.length ? { q: q.join(" AND ") } : {}; // can't return empty string since google api will fail
};

const parseExtensionFromMimeType = (mimeType: string): string => {
	const type = mimeType.split(".").pop() || "";

	return type ? `Google ${type.charAt(0).toUpperCase() + type.slice(1)}` : "";
};

const ensureDoubleDigits = (number: number) => (`0${number}`).slice(-2);

const parseDuration = (durationInMs: number) => {
	let seconds = durationInMs / 1000;
	const hours = parseInt(seconds / 3600);
	seconds %= 3600;
	const minutes = parseInt(seconds / 60);
	seconds = Math.floor(seconds % 60);

	return `${hours ? `${ensureDoubleDigits(hours)}:` : ""}${`${ensureDoubleDigits(minutes)}:`}${ensureDoubleDigits(seconds)}`;
};

const parseMetadata = ({ imageMediaMetadata, videoMediaMetadata }: Object) => {
	let metadata = "";


	if (imageMediaMetadata && (imageMediaMetadata.width > 0 || imageMediaMetadata.height > 0)) {
		metadata = `${imageMediaMetadata.width}x${imageMediaMetadata.height}`;
	}

	if (videoMediaMetadata && videoMediaMetadata.durationMillis) {
		const duration = parseDuration(videoMediaMetadata.durationMillis);
		metadata = `${duration}`;
	}

	return metadata;
};


export const removeExtension = (title: string, extension: string) => {
	const dotExtension = `.${extension}`;

	return title.endsWith(dotExtension) ? title.slice(0, (title.length - dotExtension.length)) : title;
};


const isDownloadableItem = (item) => (
	item.capabilities?.canDownload === false ?
	false :
	!appConfig.google.blackListApps.some( (app) => new RegExp(`^application/vnd.google-apps.${app}$`).test(item.mimeType))
);

// TODO: check if this logic can be moved to reducer
export const prepareItems = (
	items: Array<Object>,
	{ driveId, path = [] , accessToken}: Object = {},
): Array<GoogleDriveItem> => items.filter(isDownloadableItem).map((item) => {

	const {
		webViewLink = "",
		webContentLink = "",
		thumbnailLink = "",
		mimeType = "",
		kind = "",
		fileExtension,
		imageMediaMetadata,
		videoMediaMetadata,
		id,
		parents,
		...other
	} = item;

	const isFolder = isDrive(kind) || mimeType.includes("folder");

	const itemKind = isDrive(kind)
		? KIND.DRIVE
		: isFolder
			? KIND.FOLDER
			: KIND.FILE;

	const extension = isFolder
		? ""
		: fileExtension || parseExtensionFromMimeType(mimeType);

	const metadata = parseMetadata({
		imageMediaMetadata,
		videoMediaMetadata,
	});

	let thumbnailUrl = thumbnailLink;

	if(thumbnailLink && accessToken){
		const url = new URL(thumbnailLink);
		url.searchParams.append("access_token", accessToken);
		thumbnailUrl = url.toString();
	}

	const obj = {
		...other,
		id,
		parents,
		extension,
		metadata,
		...getImageDimensions(item),
		thumbnail: thumbnailUrl,
		link: webViewLink,
		url: webContentLink,
		title: removeExtension(item.name, extension),
		type: isFolder ? MIME_TYPE.FOLDER : MIME_TYPE.FILE,
		kind: itemKind,
		driveId: isDrive(kind) ? item.id : driveId,
		path: [...path, id],
		mimeType,
	};

	// folders current path is determine by the parent who "fetched" them.
	// that can also be "starred", "recent", etc.
	// we store the tree hierarchy path in addition to it's current path.
	if (path[0] === PREDEFINED_TREE_NODES.MY_DRIVE) {
		obj.treePath = [...path, id];
	}

	return obj;
});

const isAllDriveByKind = (kind): boolean => kind === KIND.STARRED
    || kind === KIND.SHARED_WITH_ME
    || kind === KIND.RECENT;

export const composeCorpora = ({
	driveId,
	term,
	kind,
}: Object): { corpora: string } => ({
	corpora: driveId
		? CORPORA.DRIVE
		: term || isAllDriveByKind(kind)
			? CORPORA.ALL_DRIVES
			: CORPORA.USER,
});

export const composeDriveParams = ({ kind, driveId, term }: Object) => {
	const isTeam = kind === KIND.SHARED_WITH_ME
        || kind === KIND.RECENT
        || kind === KIND.STARRED
        || kind === KIND.DRIVE
        || term
        || driveId;

	return {
		includeTeamDriveItems: !!isTeam,
		supportsTeamDrives: !!isTeam,
		driveId
	};
};
